import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  editCorporateEmployee,
  getCorporateEmployeeById,
} from '../../../../api/endpoints/corporateEmployee';
import type { CorporateEmployee } from '../../../../api/types/corporateEmployee';
import { useAuthStore } from '../../../../auth/authStore';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { IdCardSection } from '../../employee/components/IdCardSection';
import { PassportSection } from '../../employee/components/PassportSection';

const FIELDS: { name: keyof CorporateEmployee; label: string; readOnly?: boolean }[] = [
  { name: 'name', label: 'Ad' },
  { name: 'surName', label: 'Soyad' },
  { name: 'emailAdress', label: 'E-posta' },
  { name: 'phoneNumber', label: 'Telefon' },
  { name: 'dateOfBirth', label: 'Doğum Tarihi (GG.AA.YYYY)' },
  { name: 'countryOfResidance', label: 'İkamet Ülkesi' },
  { name: 'cityOfResidance', label: 'İkamet Şehri' },
  { name: 'registrationNumber', label: 'Sicil No', readOnly: true },
  { name: 'policyName', label: 'Politika', readOnly: true },
  { name: 'costCenterName', label: 'Masraf Merkezi', readOnly: true },
  { name: 'managerName', label: 'Yönetici', readOnly: true },
];

/**
 * "Firma Profilim" — self-service corporate profile. No dedicated API endpoint: binds
 * to the real `CorporateEmployee/GetCorporateEmployeeById` + `EditCorporateEmployee`
 * for the logged-in user's employee record (`authStore.employeeId`), with the ID card
 * / passport sub-sections reused from the admin employee-detail page.
 */
export default function CorporateProfilePage() {
  const { enqueueSnackbar } = useSnackbar();
  const employeeId = useAuthStore((s) => s.employeeId);
  const [form, setForm] = useState<Partial<CorporateEmployee>>({});

  const query = useQuery({
    queryKey: ['corporateProfile', employeeId],
    queryFn: () => getCorporateEmployeeById(employeeId as number),
    enabled: !!employeeId,
  });

  const employee = query.data?.corporateEmployeeList?.[0] ?? null;

  useEffect(() => {
    if (employee) setForm(employee);
  }, [employee]);

  const save = useMutation({
    mutationFn: () =>
      editCorporateEmployee({ ...(employee as CorporateEmployee), ...form } as CorporateEmployee),
    onSuccess: () => enqueueSnackbar('Profil güncellendi.', { variant: 'success' }),
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Güncelleme başarısız.', { variant: 'error' }),
  });

  if (!employeeId) {
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Typography variant="h5" gutterBottom>
          Firma Profilim
        </Typography>
        <Alert severity="info">Bu oturuma bağlı bir kurumsal personel kaydı bulunamadı.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 860 }}>
      <Typography variant="h5" gutterBottom>
        Firma Profilim
      </Typography>

      <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />
      {query.isPending && <CircularProgress size={24} />}

      {employee && (
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                {FIELDS.map((field) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={field.name}>
                    <TextField
                      fullWidth
                      size="small"
                      label={field.label}
                      value={(form[field.name] as string) ?? ''}
                      disabled={field.readOnly}
                      onChange={(e) => setForm((p) => ({ ...p, [field.name]: e.target.value }))}
                    />
                  </Grid>
                ))}
              </Grid>
              <Stack direction="row" sx={{ mt: 2 }}>
                <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Kaydediliyor…' : 'Kaydet'}
                </Button>
              </Stack>
              <ApiErrorAlert error={save.error} sx={{ mt: 2 }} />
            </CardContent>
          </Card>

          <IdCardSection employeeID={employeeId} />
          <PassportSection employeeID={employeeId} />
        </Stack>
      )}
    </Box>
  );
}
